import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { ShareNetwork, Copy, ArrowClockwise, Trash, LinkSimple, ArrowSquareOut } from "@phosphor-icons/react";

export function ShareReport({ businessId, shareId, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const origin = window.location.origin;
  const link = shareId ? `${origin}/report/${shareId}` : "";

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/share`);
      onChange(data.share_id);
      toast.success(shareId ? "New link generated (old one revoked)" : "Share link created");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/businesses/${businessId}/share`);
      onChange(null);
      toast.success("Share link revoked");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = link;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Link copied");
      } catch {
        toast.error("Couldn't copy — select the link and copy manually");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="share-report-btn" className="px-5 py-2.5 rounded-md text-sm font-semibold border inline-flex items-center gap-2 transition-colors hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-text)", color: "var(--bk-text)" }}>
          <ShareNetwork size={18} weight="duotone" /> Share report
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Client-shareable report</DialogTitle>
        </DialogHeader>
        <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>
          Anyone with this link can view a clean, printable, read-only progress report — no login required.
          The link is unguessable; regenerate or revoke it anytime.
        </p>

        {shareId ? (
          <div className="space-y-4 mt-2">
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-md border" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }}>
              <LinkSimple size={18} color="var(--bk-text-2)" />
              <input data-testid="share-link-input" readOnly value={link} className="flex-1 bg-transparent text-sm outline-none font-mono" style={{ color: "var(--bk-text)" }} />
              <button onClick={copy} data-testid="copy-share-link" className="p-1.5 rounded hover:bg-white" title="Copy">
                <Copy size={18} weight="duotone" color="var(--bk-accent)" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={link} target="_blank" rel="noreferrer" data-testid="open-report-link" className="bk-btn-primary px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2">
                <ArrowSquareOut size={16} weight="bold" /> Open report
              </a>
              <button onClick={generate} disabled={busy} data-testid="regenerate-share" className="px-4 py-2 rounded-md text-sm font-semibold border inline-flex items-center gap-2 hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                <ArrowClockwise size={16} weight="bold" /> Regenerate
              </button>
              <button onClick={revoke} disabled={busy} data-testid="revoke-share" className="px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2" style={{ color: "#B23B1F" }}>
                <Trash size={16} weight="bold" /> Revoke
              </button>
            </div>
          </div>
        ) : (
          <button onClick={generate} disabled={busy} data-testid="create-share-link" className="bk-btn-primary px-6 py-2.5 rounded-md text-sm font-semibold inline-flex items-center gap-2 mt-2 disabled:opacity-60">
            <LinkSimple size={18} weight="bold" /> {busy ? "Creating…" : "Create share link"}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
